import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider, useBranding } from "@/context/BrandingContext";
import { CreatorLayout, AdminLayout } from "@/components/layouts";
import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import { ForgotPassword, ResetPassword } from "@/pages/auth/Password";
import CreatorDashboard from "@/pages/creator/Dashboard";
import { CreatorDeals, CreatorDealDetail } from "@/pages/creator/Deals";
import CreatorEarnings from "@/pages/creator/Earnings";
import { ChannelFeed, ChannelPostPage } from "@/pages/creator/Channel";
import CreatorProfile from "@/pages/creator/Profile";
import CreatorMessages from "@/pages/creator/Messages";
import NotificationsPage from "@/pages/Notifications";
import AdminOverview from "@/pages/admin/Overview";
import AdminCreators from "@/pages/admin/Creators";
import AdminCreatorDetail from "@/pages/admin/CreatorDetail";
import AdminCategories from "@/pages/admin/Categories";
import AdminDeals from "@/pages/admin/Deals";
import { AdminEarnings, AdminPayments } from "@/pages/admin/Finance";
import AdminChannel from "@/pages/admin/Channel";
import AdminBroadcasts from "@/pages/admin/Broadcasts";
import AdminMessages from "@/pages/admin/Messages";
import { AdminBranding, AdminPrivacy, AdminSettings, AdminNotifications } from "@/pages/admin/Settings";

const Splash = () => (
  <div className="min-h-screen grid place-items-center" data-testid="app-loading"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
);

function Guard({ role, children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  return children;
}

function AppRoutes() {
  const { config } = useBranding();
  if (!config) return <Splash />;
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<Guard role="creator"><CreatorLayout /></Guard>}>
        <Route path="/dashboard" element={<CreatorDashboard />} />
        <Route path="/deals" element={<CreatorDeals />} />
        <Route path="/deals/:id" element={<CreatorDealDetail />} />
        <Route path="/earnings" element={<CreatorEarnings />} />
        <Route path="/channel" element={<ChannelFeed />} />
        <Route path="/channel/:id" element={<ChannelPostPage />} />
        <Route path="/profile" element={<CreatorProfile />} />
        <Route path="/messages" element={<CreatorMessages />} />
        <Route path="/notifications" element={<NotificationsPage />} />
      </Route>
      <Route path="/admin" element={<Guard role="admin"><AdminLayout /></Guard>}>
        <Route index element={<AdminOverview />} />
        <Route path="creators" element={<AdminCreators />} />
        <Route path="creators/:id" element={<AdminCreatorDetail />} />
        <Route path="categories" element={<AdminCategories />} />
        <Route path="deals" element={<AdminDeals />} />
        <Route path="earnings" element={<AdminEarnings />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="channel" element={<AdminChannel />} />
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="broadcasts" element={<AdminBroadcasts />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="branding" element={<AdminBranding />} />
        <Route path="privacy" element={<AdminPrivacy />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
      <Route path="*" element={<Navigate to={config.setup_required ? "/signup" : "/login"} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App dark">
      <BrandingProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster theme="dark" position="top-center" richColors />
        </AuthProvider>
      </BrandingProvider>
    </div>
  );
}
